// src/pages/Accounting/ChartOfAccounts/components/Toolbar.tsx
// Chart of accounts toolbar (right-side action buttons on the populated view).
// Clean up ▾ · Run report · Vela N for you · + New account ▾
// Clean up dropdown and New account dropdown live inline here.

import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown, Plus, Sparkles, Archive, GitMerge, CheckCircle2, Clock,
  BarChart3, FileUp, Rows, Pencil,
} from "lucide-react";

interface ToolbarProps {
  velaCount: number;
  onVelaClick: () => void;
  onNewAccount: () => void;
  onRunReport: () => void;
  onImportChartOfAccounts: () => void;
  onCreateMultiple: () => void;
  onStartVelaCleanup: () => void;
  onArchiveUnused: () => void;
  onMergeDuplicates: () => void;
  onCategorizeUncategorized: () => void;
  onOpenEditMode: () => void;
  onViewCleanupHistory: () => void;
}

export default function Toolbar(props: ToolbarProps) {
  const [cleanOpen, setCleanOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const cleanRef = useRef<HTMLDivElement>(null);
  const newRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cleanOpen && !newOpen) return;
    const close = (e: MouseEvent) => {
      if (cleanRef.current && !cleanRef.current.contains(e.target as Node)) setCleanOpen(false);
      if (newRef.current && !newRef.current.contains(e.target as Node)) setNewOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [cleanOpen, newOpen]);

  const actClean = (fn: () => void) => () => { fn(); setCleanOpen(false); };
  const actNew = (fn: () => void) => () => { fn(); setNewOpen(false); };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Clean up ▾ */}
      <div ref={cleanRef} className="relative">
        <button
          type="button"
          onClick={() => setCleanOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-line bg-white text-ink text-[12.5px] font-semibold cursor-pointer hover:bg-[#FAFBFB]"
        >
          Clean up
          <ChevronDown size={13} strokeWidth={2.5} className="text-ink3" />
        </button>
        {cleanOpen && (
          <div className="absolute right-0 top-full mt-1.5 w-[320px] bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.12)] py-1.5 z-50 overflow-hidden">
            <DropdownItem icon={<VelaDot />} label="Start Vela cleanup" meta="Walk through all suggestions one at a time" onClick={actClean(props.onStartVelaCleanup)} />
            <div className="h-px bg-lineSoft my-1" />
            <DropdownItem icon={<Archive size={16} strokeWidth={2} className="text-ink3" />} label="Archive unused accounts" meta="Accounts quiet 90+ days" onClick={actClean(props.onArchiveUnused)} />
            <DropdownItem icon={<GitMerge size={16} strokeWidth={2} className="text-ink3" />} label="Merge duplicates" meta="Overlapping accounts" onClick={actClean(props.onMergeDuplicates)} />
            <DropdownItem icon={<CheckCircle2 size={16} strokeWidth={2} className="text-ink3" />} label="Categorize uncategorized" meta="Review flagged transactions" onClick={actClean(props.onCategorizeUncategorized)} />
            <div className="h-px bg-lineSoft my-1" />
            <DropdownItem icon={<Pencil size={16} strokeWidth={2} className="text-ink3" />} label="Open chart of accounts in edit mode" meta="Rename, change type, lock, archive" onClick={actClean(props.onOpenEditMode)} />
            <DropdownItem icon={<Clock size={16} strokeWidth={2} className="text-ink3" />} label="View cleanup history" meta="Past cleanup actions" onClick={actClean(props.onViewCleanupHistory)} />
          </div>
        )}
      </div>

      {/* Run report */}
      <button
        type="button"
        onClick={props.onRunReport}
        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-line bg-white text-ink text-[12.5px] font-semibold cursor-pointer hover:bg-[#FAFBFB]"
      >
        <BarChart3 size={13} strokeWidth={2} className="text-ink3" />
        Run report
      </button>

      {/* Vela N for you */}
      {props.velaCount > 0 && (
        <button
          type="button"
          onClick={props.onVelaClick}
          className="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-vela-fade border border-vela-wash text-vela-deep text-[12.5px] font-bold cursor-pointer hover:brightness-95"
        >
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#6B46C1] text-white text-[9px] font-extrabold inline-flex items-center justify-center">
            V
          </span>
          {props.velaCount} for you
        </button>
      )}

      {/* + New account ▾ (split button) */}
      <div ref={newRef} className="relative">
        <div className="inline-flex items-center rounded-lg bg-teal-dark text-white overflow-hidden">
          <button
            type="button"
            onClick={props.onNewAccount}
            className="inline-flex items-center gap-1.5 h-9 pl-3.5 pr-3 text-[12.5px] font-semibold border-none bg-transparent cursor-pointer hover:brightness-110"
          >
            <Plus size={13} strokeWidth={2.5} />
            New account
          </button>
          <button
            type="button"
            onClick={() => setNewOpen((v) => !v)}
            className="h-9 w-8 inline-flex items-center justify-center border-l border-white/30 bg-transparent text-white cursor-pointer hover:brightness-110"
            aria-label="More new account options"
          >
            <ChevronDown size={13} strokeWidth={2.5} />
          </button>
        </div>
        {newOpen && (
          <div className="absolute right-0 top-full mt-1.5 w-[280px] bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.12)] py-1.5 z-50 overflow-hidden">
            <DropdownItem icon={<Plus size={16} strokeWidth={2} className="text-ink3" />} label="New account" meta="Add a single account" onClick={actNew(props.onNewAccount)} />
            <DropdownItem icon={<FileUp size={16} strokeWidth={2} className="text-ink3" />} label="Import chart of accounts" meta="Upload CSV or Excel" onClick={actNew(props.onImportChartOfAccounts)} />
            <DropdownItem icon={<Rows size={16} strokeWidth={2} className="text-ink3" />} label="Create multiple at once" meta="Paste or add in bulk" onClick={actNew(props.onCreateMultiple)} />
          </div>
        )}
      </div>
    </div>
  );
}

function DropdownItem({ icon, label, meta, onClick }: { icon: React.ReactNode; label: string; meta: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="w-full flex items-start gap-2.5 px-3.5 py-2.5 text-left hover:bg-panel border-none bg-transparent cursor-pointer">
      <span className="flex-shrink-0 mt-0.5">{icon}</span>
      <span className="flex flex-col">
        <span className="text-[13px] text-ink font-medium">{label}</span>
        <span className="text-[11px] text-ink4 mt-0.5">{meta}</span>
      </span>
    </button>
  );
}

function VelaDot() {
  return (
    <span className="w-[18px] h-[18px] rounded-full bg-gradient-to-br from-[#7C3AED] to-[#6B46C1] text-white text-[10px] font-extrabold inline-flex items-center justify-center">
      <Sparkles size={10} strokeWidth={2.5} />
    </span>
  );
}